import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

type Theme = "dark" | "light";

const ThemeContext = createContext<Theme>("dark");

/** Read current theme from the HTML class list. */
function readTheme(): Theme {
  return document.documentElement.classList.contains("light")
    ? "light"
    : "dark";
}

/**
 * Theme provider that watches the `light` class on `<html>` via MutationObserver.
 * When the class changes, it swaps the key on the subtree — forcing React to
 * remount all children (triggering framer motion animations, canvas redraws, etc.).
 * Mirrors how LangLocaleProvider forces a full re-render on locale change.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>("dark");

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    setTheme(readTheme());

    const observer = new MutationObserver(() => {
      const next = readTheme();
      setTheme((prev) => (prev === next ? prev : next));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);
  /* eslint-enable react-hooks/set-state-in-effect */

  return <ThemeContext value={theme}>{children}</ThemeContext>;
}

/** Returns `"dark"` or `"light"`. Reactively updates when the theme changes. */
export function useTheme(): Theme {
  return useContext(ThemeContext);
}
